import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import MFormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import React, { useCallback, useState } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { Label } from 'reactstrap';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormSelectItem } from './FormSelect';
import { R } from '@core/utils/r';

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="fa fa-search"></i>
    </components.DropdownIndicator>
  );
};

export interface FormAjaxSelect2Props<T extends string | number | any> extends FieldProps<FormControl<T>> {
  onChange?(value: any): void;
  onValue?(value: any): void;
  withoutGroup?: boolean;
  initalValue?: FormSelectItem<T>;
  initalValues?: FormSelectItem<T>[];
  autofocus?: boolean;
  multiple?: boolean;
  callback?(search: string): Observable<FormSelectItem<T>[]>;
  noBody?: boolean;
  cacheOptions?: boolean;
  debounce?: number;
  showDropDownIndicator?: boolean;
  showFullWidth?: boolean;
  showEmptyOptions?: boolean;
}

export function FormAjaxSelect2<T extends string | number | any>(props: FormAjaxSelect2Props<T>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);
  const [value, setValue] = useState<FormSelectItem<T> | null>(props.initalValue || null);
  const [values, setValues] = useState<FormSelectItem<T>[] | null>(props.initalValues || null);
  const [searchString, setSearchString] = useState<FormSelectItem<T> | null>(props.initalValue || null); // this is the string which user types and we can have it in the field in case there is no match and the requirement requires to have the input string if no match from Ajax request
  const { cacheOptions = true, debounce = 500, showDropDownIndicator = true, showFullWidth = false, showEmptyOptions = true } = props;

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = fieldConfig.disabled || false;

  const getOptions = useCallback(
    (search: any, callback: any) => {
      setSearchString(search);

      if (props.callback) {
        props
          .callback(search)
          .pipe(tap(result => callback(result)))
          .subscribe();
      } else {
        callback([]);
      }
    },
    [props],
  );

  // This piece of code allows the user to type in the input field and if there is no match from the ajax request, the input string will be the value of the field
  const handleOnBlurForEmptyOptions = useCallback(searchStringVal => {
    if (typeof searchStringVal === 'string') {
      const newSearch = { label: searchStringVal, value: { id: '', name: searchStringVal } };
      formControl.setValue((newSearch as FormSelectItem<T>).value);
      props.onChange && props.onChange((newSearch as FormSelectItem<T>).value);
      props.onValue && props.onValue(newSearch as FormSelectItem<T>);
      setValue(newSearch as FormSelectItem<T>);
    }
  }, []);

  const debouncedOptions = R.debounce(getOptions, debounce);

  const content = (
    <React.Fragment>
      <Label>{fieldConfig.label}</Label>
      <MFormControl error={!!fieldError} fullWidth>
        <AsyncSelect
          autoFocus={props.autofocus}
          cacheOptions={cacheOptions}
          isClearable
          isMulti={props.multiple}
          loadOptions={debouncedOptions}
          value={props.multiple ? values : value}
          readOnly={fieldReadonly}
          disabled={fieldDisabled}
          onFocus={() => formControl.onFocus()}
          onBlur={() => (showEmptyOptions ? formControl.onBlur() : handleOnBlurForEmptyOptions(searchString))}
          placeholder={fieldConfig.placeholder || `Search ${fieldConfig.label || ''}...`}
          onChange={value => {
            setSearchString(value as FormSelectItem<T> | null);
            if (!value) {
              formControl.setValue(null);
              props.onChange && props.onChange(null);
              setValue(null);
              return;
            }

            if (props.multiple) {
              formControl.setValue((value as any).map((v: any) => v.value));
              setValues(value as FormSelectItem<T>[]);
            } else {
              formControl.setValue((value as FormSelectItem<T>).value);
              props.onChange && props.onChange((value as FormSelectItem<T>).value);
              props.onValue && props.onValue(value as FormSelectItem<T>);
              setValue(value as FormSelectItem<T>);
            }
          }}
          components={{ DropdownIndicator: showDropDownIndicator ? DropdownIndicator : null }}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          menuPortalTarget={props.noBody ? undefined : document.body}
          classNamePrefix="select2-selection"
          noOptionsMessage={({ inputValue }) => (!inputValue ? 'Start typing to search...' : showEmptyOptions ? 'No options' : null)}
        />
        {!!fieldError && <FormHelperText>{fieldError}</FormHelperText>}
      </MFormControl>
    </React.Fragment>
  );

  if (props.withoutGroup) return content;
  return (
    <div className="form-group" style={showFullWidth ? { width: '100%' } : {}}>
      {content}
    </div>
  );
}
